import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiService } from './api.service';

@Injectable({
	providedIn: 'root',
})
export class InProgressService {
	private readonly BASE_PATH = 'inProgress';

	constructor(private api: ApiService) {}

	/**
	 * Check if the current user has permission to delete in-progress course work
	 * Only accessible by admin users
	 *
	 * @returns A promise that resolves to a boolean indicating if the user has the permission
	 */
	public async canDeleteInProgressCourseWork$(): Promise<boolean> {
		return firstValueFrom(
			this.api.get<boolean>(`${this.BASE_PATH}/can-delete-in-progress-course-work`)
		);
	}

	/**
	 * Check if a course is in progress
	 * Only accessible by admin users
	 *
	 * @param uuid The UUID of the course to check
	 * @returns A promise that resolves to a boolean indicating if the course is in progress
	 */
	public async isCourseInProgress$(uuid: string): Promise<boolean> {
		return firstValueFrom(this.api.get<boolean>(`${this.BASE_PATH}/course/${uuid}`));
	}

	/**
	 * Check which courses from a list are in progress
	 * Only accessible by admin users
	 *
	 * @param uuids Array of course UUIDs to check
	 * @returns A promise that resolves to an array of UUIDs that are in progress
	 */
	public async coursesInProgress$(uuids: string[]): Promise<string[]> {
		const results = await Promise.all(
			uuids.map(async uuid => {
				const isInProgress = await this.isCourseInProgress$(uuid);
				return { uuid, isInProgress };
			})
		);

		return results.filter(result => result.isInProgress).map(result => result.uuid);
	}

	/**
	 * Check if a unit is in progress
	 * Only accessible by admin users
	 *
	 * @param uuid The UUID of the unit to check
	 * @returns A promise that resolves to a boolean indicating if the unit is in progress
	 */
	public async isUnitInProgress$(uuid: string): Promise<boolean> {
		return firstValueFrom(this.api.get<boolean>(`${this.BASE_PATH}/unit/${uuid}`));
	}

	/**
	 * Check which units from a list are in progress
	 * Only accessible by admin users
	 *
	 * @param uuids Array of unit UUIDs to check
	 * @returns A promise that resolves to an array of UUIDs that are in progress
	 */
	public async unitsInProgress$(uuids: string[]): Promise<string[]> {
		const results = await Promise.all(
			uuids.map(async uuid => {
				const isInProgress = await this.isUnitInProgress$(uuid);
				return { uuid, isInProgress };
			})
		);

		return results.filter(result => result.isInProgress).map(result => result.uuid);
	}

	/**
	 * Check if a lesson is in progress
	 * Only accessible by admin users
	 *
	 * @param uuid The UUID of the lesson to check
	 * @returns A promise that resolves to a boolean indicating if the lesson is in progress
	 */
	public async isLessonInProgress$(uuid: string): Promise<boolean> {
		return firstValueFrom(this.api.get<boolean>(`${this.BASE_PATH}/lesson/${uuid}`));
	}

	/**
	 * Check which lessons from a list are in progress
	 * Only accessible by admin users
	 *
	 * @param uuids Array of lesson UUIDs to check
	 * @returns A promise that resolves to an array of UUIDs that are in progress
	 */
	public async lessonsInProgress$(uuids: string[]): Promise<string[]> {
		const results = await Promise.all(
			uuids.map(async uuid => {
				const isInProgress = await this.isLessonInProgress$(uuid);
				return { uuid, isInProgress };
			})
		);

		return results.filter(result => result.isInProgress).map(result => result.uuid);
	}

	/**
	 * Check if an activity is in progress
	 * Only accessible by admin users
	 *
	 * @param uuid The UUID of the activity to check
	 * @returns A promise that resolves to a boolean indicating if the activity is in progress
	 */
	public async isActivityInProgress$(uuid: string): Promise<boolean> {
		return firstValueFrom(this.api.get<boolean>(`${this.BASE_PATH}/activity/${uuid}`));
	}

	/**
	 * Check which activities from a list are in progress
	 * Only accessible by admin users
	 *
	 * @param uuids Array of activity UUIDs to check
	 * @returns A promise that resolves to an array of UUIDs that are in progress
	 */
	public async activitiesInProgress$(uuids: string[]): Promise<string[]> {
		const results = await Promise.all(
			uuids.map(async uuid => {
				const isInProgress = await this.isActivityInProgress$(uuid);
				return { uuid, isInProgress };
			})
		);

		return results.filter(result => result.isInProgress).map(result => result.uuid);
	}

	/**
	 * Check if content is in progress
	 * Only accessible by admin users
	 *
	 * @param uuid The UUID of the content to check
	 * @returns A promise that resolves to a boolean indicating if the content is in progress
	 */
	public async isContentInProgress$(uuid: string): Promise<boolean> {
		return firstValueFrom(this.api.get<boolean>(`${this.BASE_PATH}/content/${uuid}`));
	}

	/**
	 * Check which content items from a list are in progress
	 * Only accessible by admin users
	 *
	 * @param uuids Array of content UUIDs to check
	 * @returns A promise that resolves to an array of UUIDs that are in progress
	 */
	public async contentsInProgress$(uuids: string[]): Promise<string[]> {
		const results = await Promise.all(
			uuids.map(async uuid => {
				const isInProgress = await this.isContentInProgress$(uuid);
				return { uuid, isInProgress };
			})
		);

		return results.filter(result => result.isInProgress).map(result => result.uuid);
	}
}
